import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconCancel from '@/icons/Cancel.svg';
import IconUndo from '@/icons/Undo.svg';
import { useDiscardRequalification } from '@/models/article';
import { getAnalysisExchangeTypes } from '@/models/workflow';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { ArticleActionProps } from '@/routes/Requests/Request/Article/components/ArticleActions/ArticleActions';
import { areArticleSnashotsEqual } from '@/utils/articleSnapshot';

import { ExchangeDuration } from '../../ExchangeDuration/ExchangeDuration';
import { RepairDuration } from '../../RepairDuration/RepairDuration';
import { ActionSeparator } from '../ActionSeparator';

import { AnalyzeConfirmationDialog } from './AnalyzeConfirmationDialog';
import { RefuseAnalysisDialog } from './RefuseAnalysisDialog';

export const AnalyzeArticleAction = ({ article, onActionDone }: ArticleActionProps) => {
  const { workflow, view: requestView } = useRequestContext();
  const { view, state, hasError, iam } = useArticleContext();

  const [isOpenRefusalDialog, setIsOpenRefusalDialog] = useState(false);
  const [isOpenAnalyzeConfirmationDialog, setIsOpenAnalyzeConfirmationDialog] = useState(false);

  const repairStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'repair');

  const { mutateAsync: discard, isPending: isDiscardPending } = useDiscardRequalification({
    articleId: article.id,
  });

  const checkAnalysisErrors = () => {
    const { hasError } = state.errors.check({
      defects: view.defects.shown
        ? {
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingActions:
                view.defects.fields.toBeRepaired.shown &&
                view.defects.fields.toBeRepaired.editable &&
                view.services.actions.shown,
            },
            noDefects: view.defects.required,
          }
        : undefined,
      services: {
        actions: view.services.actions.shown
          ? {
              actions: {
                missingDescription: view.services.actions.fields.description.editable,
                missingPhotos:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable &&
                  view.services.actions.fields.photo.required,
                missingRefashionPhoto:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable,
                missingContext:
                  view.services.actions.fields.context.shown &&
                  view.services.actions.fields.context.editable &&
                  view.services.actions.fields.context.required,
                missingWorkshopPrice:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable &&
                  view.services.actions.fields.workshopPrice.required,
                workshopPriceUpdateInProgress:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable,
                organizationPriceUpdateInProgress:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable,
                spareParts: view.services.actions.fields.spareParts.shown
                  ? {
                      quantityIsNull: view.services.actions.fields.spareParts.quantity.editable,
                      missingWorkshopPrice:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable &&
                        view.services.actions.fields.spareParts.workshopPrice.required,
                      workshopPriceUpdateInProgress:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable,
                      organizationPriceUpdateInProgress:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable,
                    }
                  : undefined,
                additionalInfo: {
                  missingValue: view.services.actions.fields.additionalInfo.editable,
                },
              },
              noActions: view.services.actions.canAddAction,
            }
          : undefined,
        exchange: view.services.exchange.shown
          ? {
              missingExchangeType: !!getAnalysisExchangeTypes({ workflow, article })?.length,
              missingReplacementProduct: view.services.exchange.replacementProduct.editable,
              missingReplacementProductColor: view.details.color.shown,
              missingReplacementProductSize: view.details.size.shown,
              missingWorkshopPrice:
                requestView.workshopPrice.shown &&
                view.services.exchange.workshopPrice.editable &&
                view.services.exchange.workshopPrice.required,
              workshopPriceUpdateInProgress:
                requestView.workshopPrice.shown && view.services.exchange.workshopPrice.editable,
              organizationPriceUpdateInProgress:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable,
            }
          : undefined,
      },
    });

    return hasError;
  };

  const submitOrAskConfirmation = () => {
    const hasError = checkAnalysisErrors();

    if (hasError) {
      return;
    }

    setIsOpenAnalyzeConfirmationDialog(true);
  };

  const showResetChangesButton = !areArticleSnashotsEqual(article);

  return (
    <>
      <Footer>
        {hasError && (
          <FooterError>
            <Trans id="article.actions.analyze_article.errors">
              Please fill the missing information
            </Trans>
          </FooterError>
        )}
        <FooterButtons>
          {article?.snapshot?.serviceChoice === 'care-repair' &&
            repairStep?.config?.editRepairDueDays?.enabled && <RepairDuration />}
          {article?.snapshot?.serviceChoice === 'exchange' && <ExchangeDuration />}
          {((article?.snapshot?.serviceChoice === 'care-repair' &&
            repairStep?.config?.editRepairDueDays?.enabled) ||
            article?.snapshot?.serviceChoice === 'exchange') && <ActionSeparator />}
          <Button
            size="medium"
            variant="secondary-danger"
            onPress={() => setIsOpenRefusalDialog(true)}
            disabled={isDiscardPending}
          >
            <IconCancel />
            {!iam.hasUserScopeOnArticle('workshop') ? (
              <Trans id="article.analysis.footer.action.refuse-job.on-behalf">
                Workshop refusal
              </Trans>
            ) : (
              <Trans id="article.analysis.footer.action.refuse-job">Refuse the job</Trans>
            )}
          </Button>
          {showResetChangesButton && (
            <Button
              size="medium"
              variant="secondary"
              onPress={discard}
              isLoading={isDiscardPending}
            >
              <IconUndo />
              <Trans id="requalification-drawer.reset">Reset changes</Trans>
            </Button>
          )}
          <Button
            variant="primary"
            size="medium"
            onPress={submitOrAskConfirmation}
            disabled={isDiscardPending}
          >
            <Trans id="article.analysis.footer.action.validate">Submit quote</Trans>
          </Button>
        </FooterButtons>
      </Footer>

      <RefuseAnalysisDialog
        isOpen={isOpenRefusalDialog}
        setIsOpen={setIsOpenRefusalDialog}
        onActionDone={onActionDone}
      />

      <AnalyzeConfirmationDialog
        article={article}
        isOpen={isOpenAnalyzeConfirmationDialog}
        setIsOpen={setIsOpenAnalyzeConfirmationDialog}
        onActionDone={onActionDone}
      />
    </>
  );
};
